// the route used for a connection between an output and an input
import {style} from '../util/index.js'

export const routeMoving = {

    tackOnHorizontalTrunk(tack) {
        const trunk = tack?.cable
        const a = trunk?.wire?.[tack.segment - 1]
        const b = trunk?.wire?.[tack.segment]

        return !!(a && b && a.y === b.y)
    },

    endpointAxis(widget) {
        if (!widget?.is?.tack) return 'horizontal'

        if (widget.isEndpoint?.()) {
            return ['N', 'S'].includes(widget.endpointApproach?.()) ? 'vertical' : 'horizontal'
        }

        return this.tackOnHorizontalTrunk(widget) ? 'vertical' : 'horizontal'
    },

    adjustEndpointAxes(from, to) {
        const fromAxis = this.endpointAxis(from)
        const toAxis = this.endpointAxis(to)

        if (fromAxis === 'vertical') {
            toAxis === 'vertical' ? this.adjustVV(from.center(), to.center())
                                  : this.adjustVH(from.center(), to.center())
        }
        else {
            toAxis === 'vertical' ? this.adjustHV(from.center(), to.center())
                                  : this.adjustHH(from.center(), to.center())
        }

        this.keepEndpointApproach(from)
        this.keepEndpointApproach(to)
    },

    keepEndpointApproach(tack) {
        if (!tack?.is?.tack || !tack.isEndpoint?.() || this.wire.length < 2) return

        const approach = tack.endpointApproach?.()
        const vertical = approach === 'N' || approach === 'S'
        const direction = approach === 'N' || approach === 'W' ? -1 : 1
        const atStart = this.from === tack
        const contactIndex = atStart ? 0 : this.wire.length - 1
        const neighbourIndex = atStart ? 1 : this.wire.length - 2
        const cornerIndex = atStart ? 2 : this.wire.length - 3
        const contact = this.wire[contactIndex]
        const neighbour = this.wire[neighbourIndex]
        const corner = this.wire[cornerIndex]
        const axis = vertical ? 'y' : 'x'
        const distance = (neighbour[axis] - contact[axis]) * direction

        if (distance >= style.route.tooClose) return

        const coordinate = contact[axis] + direction * style.route.tooClose

        // A vertical-to-horizontal two-segment route has no spare corner to
        // move. Promote it once to a fixed dogleg; later adjustments only move
        // those existing points.
        if (vertical && this.wire.length === 3) {
            const other = atStart ? this.wire.at(-1) : this.wire[0]
            let middleX = (contact.x + other.x) / 2
            if (middleX === contact.x) middleX += style.route.tooClose
            const forward = [
                {...contact},
                {x: contact.x, y: coordinate},
                {x: middleX, y: coordinate},
                {x: middleX, y: other.y},
                {...other}
            ]
            this.wire.splice(0, this.wire.length, ...(atStart ? forward : forward.reverse()))
            return
        }

        if (this.wire.length < 4) return
        neighbour[axis] = coordinate
        corner[axis] = coordinate
    },

    adjustCableEndpoint(tack, otherCenter) {
        if (!tack?.cable?.is?.cable || !tack.isEndpoint?.()) return false
        if (this.wire.some((point, index) => index > 0 && (
            point.x !== this.wire[index - 1].x || point.y !== this.wire[index - 1].y
        ))) return false

        const cable = tack.cable
        cable.canonicalizeOrthogonalWire?.('endpoint adjustment')
        const wire = cable?.wire
        if (!wire || wire.length < 2) return false

        if (cable.bendEndpoint(tack, otherCenter)) {
            const tackPoint = tack.center()
            this.wire.length = 0
            if (this.from === tack) {
                this.wire.push({...tackPoint}, {...otherCenter})
            }
            else {
                this.wire.push({...otherCenter}, {...tackPoint})
            }

            tack.refreshPlacement?.()
            return true
        }

        const moveTacksOnSegment = (segment, delta, axis) => {
            for (const otherTack of cable.tacks) {
                if (otherTack.isEndpoint() || otherTack.segment != segment) continue

                axis == 'x' ? otherTack.moveX(delta) : otherTack.moveY(delta)
            }
        }

        const tackCenter = tack.center()
        const first = wire[0]
        const last = wire.at(-1)
        const firstDistance = Math.hypot(tackCenter.x - first.x, tackCenter.y - first.y)
        const lastDistance = Math.hypot(tackCenter.x - last.x, tackCenter.y - last.y)

        if (firstDistance <= lastDistance) {
            const next = wire[1]
            const horizontal = first.y === next.y
            const delta = horizontal ? otherCenter.y - first.y : otherCenter.x - first.x

            first.x = otherCenter.x
            first.y = otherCenter.y
            horizontal ? next.y = otherCenter.y : next.x = otherCenter.x
            moveTacksOnSegment(1, delta, horizontal ? 'y' : 'x')
            tack.placeOnSegment(otherCenter, 1)
        }
        else {
            const previous = wire.at(-2)
            const horizontal = previous.y === last.y
            const segment = wire.length - 1
            const delta = horizontal ? otherCenter.y - last.y : otherCenter.x - last.x

            last.x = otherCenter.x
            last.y = otherCenter.y
            horizontal ? previous.y = otherCenter.y : previous.x = otherCenter.x
            moveTacksOnSegment(segment, delta, horizontal ? 'y' : 'x')
            tack.placeOnSegment(otherCenter, segment)
        }

        const tackPoint = tack.center()
        this.wire.length = 0
        if (this.from === tack) {
            this.wire.push({...tackPoint}, {...otherCenter})
        }
        else {
            this.wire.push({...otherCenter}, {...tackPoint})
        }

        tack.refreshPlacement?.()
        cable.validateOrthogonalWire?.('endpoint adjustment')

        return true
    },

    refreshTackPlacements() {
        if (this.from?.is?.tack) this.from.refreshPlacement?.()
        if (this.to?.is?.tack) this.to.refreshPlacement?.()
    },

    // moves a segment horizontally or vertically
    moveSegment(s,delta) {

        try {

            const from = this.from
            const to = this.to

            let p1 = this.wire[s-1]
            let p2 = this.wire[s]

            // A single segment is both the first and last segment. Let its tack
            // or pad classify the movement before changing the topology; route
            // adjustment can add only the bends required by the new position.
            if (this.wire.length == 2) {
                const slidingEndpoint = (from.is.tack || from.is.pad) ? from
                                      : (to.is.tack || to.is.pad) ? to
                                      : null
                if (slidingEndpoint) return slidingEndpoint.slide(delta)
            }

            // first segment...
            if (s == 1) {
                // if one of the end points is a tack or pad - it can slide
                return (from.is.tack || from.is.pad) ? from.slide(delta) : null
            }

            // last segment...
            if (s == this.wire.length-1) {
                return (to.is.tack || to.is.pad) ? to.slide(delta) : null
            }

            // otherwise just move the segment
            if (p1.x == p2.x) {
                p1.x += delta.x
                p2.x += delta.x
            }
            else {
                p1.y += delta.y
                p2.y += delta.y
            }
        }
        finally {
            this.refreshTackPlacements()
        }
    },

    moveAllPoints(dx,dy) {
        this.wire.forEach( p => {
            p.x += dx
            p.y += dy
        })
    },

    removeOnePoint(n,p) {
        let last = p.length-1
        for (let i = n; i<last; i++)  p[i] = p[i+1]
        p.length = last      
    },

    removeTwoPoints(n,p) {
        let last = p.length-2
        for (let i = n; i<last; i++)  p[i] = p[i+2]
        p.length = last      
    },

    endDrag(s) {

        // the last point is 
        const L = this.wire.length

        // segments 1 and 2 and L-1 and L-2 are special...
        if (s==1) {
            if (this.from.is.tack || this.from.is.pad) this.from.fuseEndSegment()
        }
        else if (s==2) {
            if (this.from.is.pin) this.fuseSegmentAfter(s)
            else this.fuseSegmentBefore(s) || this.fuseSegmentAfter(s)
        }
        else if (s == L-1) {
            if (this.to.is.tack || this.to.is.pad) this.to.fuseEndSegment()
        }
        else if (s == L-2) {
            if (this.to.is.pin) this.fuseSegmentBefore(s)
            else this.fuseSegmentBefore(s) || this.fuseSegmentAfter(s)
        }
        else this.fuseSegmentBefore(s) || this.fuseSegmentAfter(s)
    },

    // check if the segment has to be fused wit previous
    fuseSegmentBefore(s) {

        // notation - a and b are the points on the segment
        const p = this.wire  

        // check
        if (s-2 < 0) return false

        const [before, a, b] = [p[s-2], p[s-1], p[s]]
        const min = style.route.tooClose

        // horizontal segment
        if (a.y == b.y) {
            // check the point before the segment
            if (Math.abs(before.y - a.y) < min) {
                b.y = before.y
                this.removeTwoPoints(s-2,p)
                return true
            }
        }
        // vertical segment
        else if (a.x == b.x) {
            // check the point before
            if (Math.abs(before.x - a.x) < min) {
                b.x = before.x
                this.removeTwoPoints(s-2,p)
                return true
            }
        }
        return false
    },

    // check if the segment has to be fused with next 
    fuseSegmentAfter(s) {

        // notation - a and b are the points on the segment
        const p = this.wire  

        // check
        if (s+1 > p.length-1) return false

        // notation
        const [a, b, after] = [p[s-1], p[s], p[s+1]]
        const min = style.route.tooClose

        // horizontal segment
        if (a.y == b.y) {

            // check the point after the segment
            if (Math.abs(after.y - b.y) < min) {
                a.y = after.y
                this.removeTwoPoints(s,p)
                return true
            }
        }
        // vertical segment
        else if (a.x == b.x) {

            // check the point after
            if (Math.abs(after.x - b.x) < min) {
                a.x = after.x
                this.removeTwoPoints(s,p)
                return true
            }
        }
        return false
    },

    // moves an endpoint of a route to a new widget position
    clampToWidget( widget ) {

        const p = this.wire
        const L = this.wire.length
        const center = widget.center()

        // if the segment is too short, change it to a four point route
        if (L < 3) return this.fourPointRoute()

        // clamp 'from' or 'to' to the center
        if (this.from == widget) {
            p[0].x = center.x
            p[0].y = center.y
            p[1].y = center.y
        }
        else if (this.to == widget) {
            p[L-1].x = center.x
            p[L-1].y = center.y
            p[L-2].y = center.y
        }
    },

    // The endpoint(s) of the route have changed - make a better route
    adjust({moveCableEndpoint = true} = {}) {
        try {
            // in autoroute situations the wire can be missing
            if (!this.wire.length) return

            // notation
            const from = this.from
            const to = this.to

            // check that there are two valid endpoints
            if ( ! to?.center || !from?.center) return

            // from new - to new
            const fn = from.center()
            const tn = to.center()

            // from previous - to previous
            const fp = this.wire[0]
            const tp = this.wire.at(-1)

            // the deltas
            const df = {x: fn.x - fp.x, y: fn.y - fp.y}
            const dt = {x: tn.x - tp.x, y: tn.y - tp.y}

            if (moveCableEndpoint && from.is.tack && this.adjustCableEndpoint(from, tn)) return
            if (moveCableEndpoint && to.is.tack && this.adjustCableEndpoint(to, fn)) return

            // check if both endpoints moved over the same distance - just move the route
            if ((df.x == dt.x) && (df.y == dt.y)) return this.moveAllPoints(df.x, df.y)

            if (from.is.tack || to.is.tack) this.adjustEndpointAxes(from, to)
            else this.adjustHH(fn,tn)
        }
        finally {
            this.refreshTackPlacements()
        }
    },

    // Starts and ends horizontally
    // Only one of the end points moves
    // a and be are the next position of the end points of the wire
    adjustHH(a, b) {

        // notation
        let p = this.wire

        // For HH there have to be at least three segments
        if (p.length < 4) return this.makeThreeSegments(a,b)

        // notation
        let last = p.length - 1
        const sMax = p.length-1
        const xMin = style.route.tooClose + 5

        // if we move the start of the route
        if (p[0].x != a.x || p[0].y != a.y) {

            // adjust the starting point
            p[0].x = a.x
            p[0].y = a.y
            p[1].y = a.y

            // check the horizontal = uneven segments, starting from the front
            for(let s = 1; s < sMax; s += 2) {
                const dx = p[s].x - p[s-1].x

                // if the segment is too short
                if (Math.abs(dx) < xMin ) {

                    // s-1 <S> s <S+1> s+1   make the segment <S> minimal length
                    p[s].x = dx > 0 ? p[s-1].x + xMin : p[s-1].x - xMin

                    // and collapse segment <S+1>
                    p[s+1].x = p[s].x
                }
            }
        }

        // move the route at the end
        if (p[last].x != b.x || p[last].y != b.y) {

            // adjust the end point
            p[last].x = b.x
            p[last].y = b.y
            p[last-1].y = b.y

            //check the horizontal = uneven segments, starting from the back
            for(let s = sMax; s > 1; s -= 2) {
                const dx = p[s].x - p[s-1].x
                if (Math.abs(dx) < xMin) {
                    p[s-1].x = dx > 0 ? p[s].x - xMin : p[s].x + xMin
                    p[s-2].x = p[s-1].x
                }
            }
        }
    },

    // horizontal / vertical
    adjustHV(a, b) {
        // maybe create an extra segment
        if (this.wire.length == 2) return this.makeTwoSegments(a, b)

        const p = this.wire
        const last = p.length-1

        p[0].x = a.x
        p[0].y = a.y
        p[1].y = a.y
        p[last].x = b.x
        p[last].y = b.y
        p[last-1].x = b.x
        p[last-1].y = p[last-2].y
    },

    // horizontal / vertical
    adjustVH(a, b) {
        // maybe create an extra segment
        if (this.wire.length == 2) return this.threePointRoute(false)

        const p = this.wire
        const last = p.length-1

        p[0].x = a.x
        p[0].y = a.y
        p[1].x = a.x
        p[last].x = b.x
        p[last].y = b.y
        p[last-1].x = p[last-2].x
        p[last-1].y = b.y
    },

    // Starts and ends vertically. Reuse the horizontal implementation with
    // transposed coordinates so there is still only one same-axis algorithm.
    adjustVV(a, b) {
        const transpose = point => [point.x, point.y] = [point.y, point.x]
        this.wire.forEach(transpose)

        try {
            this.adjustHH({x: a.y, y: a.x}, {x: b.y, y: b.x})
        }
        finally {
            this.wire.forEach(transpose)
        }
    },

    makeTwoSegments(a, b) {

        // notation
        const w = this.wire

        // reset the wire
        w.length = 0

        // set 3 points
        w.push({x: a.x, y: a.y})
        w.push({x: b.x, y: a.y})
        w.push({x: b.x, y: b.y})
    },

    makeThreeSegments(a, b) {

        // notation
        const w = this.wire

        // reset the wire
        w.length = 0

        // set four points
        w.push({x: a.x, y: a.y})
        w.push({x: (a.x+b.x)/2, y: a.y})
        w.push({x: (a.x+b.x)/2, y: b.y})
        w.push({x: b.x, y: b.y})
    },

}
